import React, { useState, useEffect } from "react";
import { Modal, DatePicker, Space, Typography, Input, Checkbox, Radio } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { notification } from "antd";
import { useMemberService } from "../../../services/members/MemberServiceProvider";
import { CreateMemberCancellationRequest } from "../../../requests/member/CreateMemberCancellationRequest";
import { useClient } from "../../../common/ClientProvider";
import { useClientConfig } from "../../../common/client/ClientConfigProvider";
import { DEFAULT_CANCELLATION_REASONS } from "../../../model/client/ClientSettings";
import { MemberCancellation } from "../../../model/members/MemberCancellation";

interface CancellationModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  membershipId: string;
  editingCancellation?: MemberCancellation | null;
  defaultDate?: Dayjs | null;
}

const CancellationModal: React.FC<CancellationModalProps> = ({
  open,
  onCancel,
  onSuccess,
  membershipId,
  editingCancellation,
  defaultDate,
}) => {
  const [cancellationDate, setCancellationDate] = useState<Dayjs | null>(null);
  const [cancellationReason, setCancellationReason] = useState<string>('');
  const [cancellationNotes, setCancellationNotes] = useState<string>('');
  const [cancellationActive, setCancellationActive] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const memberService = useMemberService();
  const client = useClient();
  const { cancellation } = useClientConfig();
  const reasonOptions = cancellation?.reasons ?? DEFAULT_CANCELLATION_REASONS;

  // An existing cancellation may hold free text from before the option list existed (or a code the
  // client has since removed). Show it as a selectable entry instead of silently resetting the
  // operator's radio to nothing — editing an unrelated field must not rewrite the stored reason.
  const legacyReason =
    editingCancellation?.reason && !reasonOptions.some((o) => o.value === editingCancellation.reason)
      ? editingCancellation.reason
      : null;

  useEffect(() => {
    if (open) {
      if (editingCancellation) {
        // Editing mode - populate with existing data
        setCancellationDate(dayjs(editingCancellation.cancellationDate));
        setCancellationReason(editingCancellation.reason || '');
        setCancellationNotes(editingCancellation.notes || '');
        setCancellationActive(editingCancellation.cancellationActive);
      } else {
        // Creating mode - use defaults
        setCancellationDate(defaultDate || dayjs());
        setCancellationReason('');
        setCancellationNotes('');
        setCancellationActive(true);
      }
    }
  }, [open, editingCancellation, defaultDate]);

  const handleSubmit = async () => {
    if (!cancellationDate) {
      notification.error({
        message: "Error",
        description: "Please select a cancellation date",
      });
      return;
    }

    if (!cancellationReason) {
      notification.error({
        message: "Error",
        description: "Please select a reason for leaving",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const request: CreateMemberCancellationRequest = {
        membershipId: membershipId,
        clientName: client.currentClientId,
        cancellationDate: cancellationDate.format('YYYY-MM-DD'),
        cancellationActive: cancellationActive,
        // Always a configured option code — the old free-text 'Scheduled cancellation' fallback is
        // exactly the kind of non-reason FORT-350 removes, and would show as its own row in the
        // Reason for Leaving breakdown. The OK button is disabled until a reason is picked.
        cancellationReason: cancellationReason,
        cancellationNotes: cancellationNotes,
      };

      await memberService.cancelMembership(request);

      notification.success({
        message: "Success",
        description: editingCancellation 
          ? "Cancellation updated successfully" 
          : "Membership cancellation has been scheduled",
      });

      // Reset form
      setCancellationDate(null);
      setCancellationReason('');
      setCancellationNotes('');
      setCancellationActive(true);
      
      onSuccess();
    } catch (error) {
      notification.error({
        message: "Error",
        description: editingCancellation
          ? "Failed to update cancellation"
          : "Failed to schedule membership cancellation",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setCancellationDate(null);
    setCancellationReason('');
    setCancellationNotes('');
    setCancellationActive(true);
    onCancel();
  };

  return (
    <Modal
      title={editingCancellation ? "Edit Cancellation" : "Schedule Cancellation"}
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      okText={editingCancellation ? "Update" : "Schedule"}
      cancelText="Cancel"
      okButtonProps={{ 
        danger: true,
        loading: isSubmitting,
        disabled: isSubmitting || !cancellationDate || !cancellationReason,
      }}
      cancelButtonProps={{
        disabled: isSubmitting,
      }}
    >
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <div>
          <Typography.Text>Cancellation Date:</Typography.Text>
          <DatePicker
            value={cancellationDate}
            onChange={(date) => setCancellationDate(date)}
            style={{ width: "100%", marginTop: "8px" }}
            format="DD/MM/YYYY"
          />
        </div>
        <div>
          <Typography.Text>Reason for Leaving:</Typography.Text>
          <Radio.Group
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            style={{ marginTop: "8px", display: "block" }}
            data-testid="cancellation-reason"
          >
            <Space direction="vertical">
              {reasonOptions.map((option) => (
                <Radio key={option.value} value={option.value}>
                  {option.label}
                </Radio>
              ))}
              {legacyReason && (
                <Radio value={legacyReason}>{legacyReason} (recorded previously)</Radio>
              )}
            </Space>
          </Radio.Group>
        </div>
        <div>
          <Typography.Text>Notes (optional):</Typography.Text>
          <Input.TextArea
            value={cancellationNotes}
            onChange={(e) => setCancellationNotes(e.target.value)}
            placeholder="Anything else worth recording..."
            rows={3}
            style={{ marginTop: "8px" }}
            data-testid="cancellation-notes"
          />
        </div>
        {editingCancellation && (
          <div>
            <Typography.Text>Status:</Typography.Text>
            <div style={{ marginTop: "8px" }}>
              <Checkbox
                checked={cancellationActive}
                onChange={(e) => setCancellationActive(e.target.checked)}
              >
                Active
              </Checkbox>
            </div>
          </div>
        )}
      </Space>
    </Modal>
  );
};

export default CancellationModal;

